import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../../shared/storage/firebase';
import { safeGameUrl } from './gameMedia';
import { LibraryItem } from './types';

export function nominationPublishIssue(item: LibraryItem): string | null {
  if (!item.title.trim()) return 'Add a title before publishing';
  if (item.contentType === 'game') {
    if (!safeGameUrl(item.game?.url)) return 'Add a valid game link before publishing';
    if (item.game?.embedUrl && !safeGameUrl(item.game.embedUrl)) return 'Fix the game embed link before publishing';
  } else if (item.contentType === 'shot_batch') {
    if (!item.shotBatch?.videos.length || item.shotBatch.videos.some(video => !video.url.trim())) return 'Add the shot videos before publishing';
  } else if (!item.displayAssetUrl?.trim()) return 'Add media before publishing';
  return null;
}

// Each item commits independently, so a failed nomination can be retried without
// republishing successful items. Read fresh data to respect other reviewers.
export async function publishNomination(id: string, userId: string, publishedCount: number, schoolLogoUrl?: string) {
  const itemRef = doc(db, 'libraryItems', id);
  const sequenceRef = doc(db, 'libraryMetadata', 'collectibleSequence');
  await runTransaction(db, async transaction => {
    const [snapshot, sequence] = await Promise.all([transaction.get(itemRef), transaction.get(sequenceRef)]);
    if (!snapshot.exists()) throw new Error('This nomination no longer exists');
    const item = { ...snapshot.data(), id } as LibraryItem;
    if (item.status === 'published') return;
    if (item.status !== 'nominated' && item.status !== 'review') throw new Error('This item is no longer in the nomination queue');
    const issue = nominationPublishIssue(item);
    if (issue) throw new Error(issue);
    const existingNumber = Number(item.collectibleNumber || 0);
    const collectibleNumber = existingNumber || Math.max(Number(sequence.data()?.lastNumber || 0), publishedCount) + 1;
    transaction.update(itemRef, {
      status: 'published', visibility: 'public', publishedBy: userId, reviewedBy: userId,
      publishedAt: serverTimestamp(), updatedAt: serverTimestamp(), collectibleNumber,
      ...(!item.defaultsVersion ? { defaultsVersion: 2, gameEligible: ['character', 'world'].includes(item.contentType), featured: true } : {}),
      ...(!item.schoolLogoUrl && schoolLogoUrl ? { schoolLogoUrl } : {}),
    });
    if (!existingNumber) transaction.set(sequenceRef, { lastNumber: collectibleNumber, updatedAt: serverTimestamp() }, { merge: true });
  });
}
